import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import UploadFileInput from '@repo/ui/file-input'
import { UploadIcon } from '@repo/ui/icons'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z, ZodType } from 'zod'

import TextInput from '../../../../../../packages/ui/components/atoms/input/text-input'
import useTranslate from '../../../lib/hooks/use-translate'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ValidationButtons from '../validation-buttons'

type UploadFileInput = {
  file: IFile
}

type Props = BaseFormProps<UploadFileInput> & {
  allowedTypes: FileTypeEnum[]
  allowCaption?: boolean
}

const UPLOAD_FILE_SCHEMA = (language: AvailableLanguages): ZodType<UploadFileInput> =>
  z.object({ file: FILE_VALIDATION({ language }) })

export function UploadFileForm({
  defaultValues,
  className,
  allowedTypes,
  allowCaption,
  ...validationButtonProps
}: Props) {
  const { t, currentLanguage } = useTranslate()
  const form = useForm<UploadFileInput>({
    resolver: zodResolver(UPLOAD_FILE_SCHEMA(currentLanguage)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'divide-y divide-white-active'}>
          <div className={cn('flex flex-col gap-3', className)}>
            {allowCaption && (
              <TextInput
                type={'textarea'}
                label={t('file.caption')}
                value={form.watch('file.caption')}
                onChange={(e) => form.setValue('file.caption', e.target.value)}
              />
            )}

            <FormField
              control={form.control}
              name={'file'}
              render={({ field, fieldState }) => {
                return (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <UploadFileInput
                        placeholder={t('file.upload.placeholder')}
                        types={allowedTypes}
                        icon={UploadIcon}
                        hasError={!!fieldState.error}
                        label={t('file.upload.label')}
                        files={field?.value ? [field?.value] : undefined}
                        onFilesChange={(files) => {
                          if (files?.length === 0) field.onChange(null)
                          else {
                            // Preserve the caption when updating the file
                            const newFile = files[0]
                            if (allowCaption) {
                              const currentCaption = form.getValues('file.caption')?.trim()
                              newFile.caption = currentCaption ? currentCaption : newFile.name
                            }

                            field.onChange(newFile)
                          }
                        }}
                        onFileError={(error) => {
                          if (error) toast.error(error)
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>

          <div className="pb-6">
            <ValidationButtons
              hasBorderTop={false}
              buttonsConfig={validationButtonProps.buttonsConfig}
            />
          </div>
        </div>
      </form>
    </Form>
  )
}
